/* eslint-disable no-console */
import { checkProps } from '@helpers/utilities';

// Remove trailing slash from siteUrl.
const siteUrlNoSlash =
  typeof window !== `undefined` ? window.location.origin : ``;

// Circle_Branch is added for pre SSR references.
const environment = process.env.GATSBY_ENVIRONMENT;

export const initDataLayer = () => {
  const isBrowser = typeof window !== `undefined`;
  if (isBrowser) {
    window.dataLayer = window.dataLayer || [];
  }
};

const dataLayerPush = ({ event, page, eventData, ecommerce, dataSource }) => {
  // Create the datalayer if it doesn't exist.
  window.dataLayer = window.dataLayer || [];

  const dataLayerObject = {};

  checkProps({
    fields: [{ name: `event`, value: event }]
  });

  dataLayerObject.event = event;

  if (typeof page !== `undefined`) {
    dataLayerObject.page = page;
  }

  if (typeof eventData !== `undefined`) {
    dataLayerObject.eventData = eventData;
  }

  if (typeof ecommerce !== `undefined`) {
    dataLayerObject.ecommerce = ecommerce;
  }

  if (typeof dataSource !== `undefined`) {
    dataLayerObject.data_source = dataSource;
  }

  if (typeof window !== `undefined`) {
    switch (environment) {
      case `master`:
        window.dataLayer.push({ ...dataLayerObject });
        break;
      case `staging`:
        window.dataLayer.push({ ...dataLayerObject });
        break;
      case `develop`:
        window.dataLayer.push({ ...dataLayerObject });
        break;
      default:
        console.log(JSON.stringify(dataLayerObject, null, 2));
    }
  }
};

export const dataLayerPage = ({ event, seo, location }) => {
  checkProps({
    fields: [
      { name: `event`, value: event },
      { name: `seo`, value: seo },
      { name: `location`, value: location }
    ]
  });

  const pathTypes = {
    home: `Home`,
    blog: `Articles`,
    products: `Products`,
    contact: `Contact`,
    'product-finder': `Product Finder`,
    'finder-results': `Product Finder Results`,
    faqs: `FAQ`,
    acnecide: `Content`,
    purifide: `Content`,
    'skincare-routine': `Content`,
    'where-to-buy': `Retailers`,
    'privacy-policy': `Legal`,
    cookies: `Legal`
  };

  const paths = location.pathname.split(`/`).filter((p) => p !== ``);

  const page = {
    path: location.pathname,
    title: seo.metaTitle,
    type: paths.length > 0 ? pathTypes[paths[0]] || `none` : pathTypes.home
  };

  const dataSource = {
    environment: environment || `local`,
    platform: `web`
  };

  dataLayerPush({ event, page, dataSource });
};

export const dataLayerClick = ({ event, clickText, clickUrl }) => {
  checkProps({
    fields: [
      { name: `event`, value: event },
      { name: `clickText`, value: clickText },
      { name: `clickUrl`, value: clickUrl }
    ]
  });

  const eventData = {
    click_text: clickText,
    click_url: `${siteUrlNoSlash}${clickUrl}`
  };

  dataLayerPush({ event, eventData });
};

export const dataLayerFaqClick = ({ event, clickText, interactionType }) => {
  checkProps({
    fields: [
      { name: `event`, value: event },
      { name: `clickText`, value: clickText },
      { name: `interactionType`, value: interactionType }
    ]
  });

  const eventData = {
    click_text: clickText,
    interaction_type: interactionType
  };

  dataLayerPush({ event, eventData });
};

export const dataLayerBuyNowClick = ({ event }) => {
  checkProps({
    fields: [{ name: `event`, value: event }]
  });

  dataLayerPush({ event });
};

export const dataLayerRetailerClick = ({ event, retailer, brand, product }) => {
  checkProps({
    fields: [
      { name: `event`, value: event },
      { name: `retailer`, value: retailer },
      { name: `brand`, value: brand },
      { name: `product`, value: product }
    ]
  });

  const eventData = {
    retailer,
    item_brand: brand,
    item_name: product
  };

  dataLayerPush({ event, eventData });
};

export const dataLayerStockistClick = ({ event }) => {
  checkProps({
    fields: [{ name: `event`, value: event }]
  });

  dataLayerPush({ event });
};

export const dataLayerProductClick = ({
  event,
  productId,
  productName,
  productIndex,
  productBrand,
  productPrice
}) => {
  checkProps({
    fields: [
      { name: `event`, value: event },
      { name: `productId`, value: productId },
      { name: `productName`, value: productName },
      { name: `productBrand`, value: productBrand },
      { name: `productPrice`, value: productPrice },
      { name: `productIndex`, value: productIndex }
    ]
  });

  const ecommerce = {
    items: [
      {
        item_id: productId,
        item_name: productName,
        item_brand: productBrand,
        index: productIndex,
        price: productPrice
      }
    ]
  };

  dataLayerPush({ event, ecommerce });
};

export const dataLayerProductViewPage = ({
  event,
  products,
  filters = null
}) => {
  checkProps({
    fields: [{ name: `event`, value: event }]
  });

  let str = `N/A`;

  if (filters !== null) {
    str = Array.prototype.map.call(filters, (s) => s.title).toString();
  }

  const eventData = {
    plpPage: {
      product_count: products.length,
      all_filters_applied: str
    }
  };

  const ecommerce = {
    items: []
  };

  products.map((product) => {
    return ecommerce.items.push({
      item_id: product.id,
      item_name: product.title,
      item_brand:
        typeof product.brand === `undefined` ||
        product.brand === null ||
        typeof product.brand.title === `undefined` ||
        product.brand.title === null
          ? `Unknown Brand`
          : product.brand.title,
      price: product.price
    });
  });

  dataLayerPush({ event, ecommerce, eventData });
};

export const dataLayerProductViewFilterPage = ({
  event,
  products,
  filters = null
}) => {
  checkProps({
    fields: [{ name: `event`, value: event }]
  });

  let str = `N/A`;

  if (filters !== null) {
    str = Array.prototype.map.call(filters, (s) => s.title).toString();
  }

  const eventData = {
    plpPage: {
      product_count: products.length,
      all_filters_applied: str
    }
  };

  dataLayerPush({ event, eventData });
};

export const dataLayerVideos = ({
  event,
  url,
  status,
  percentage,
  duration
}) => {
  checkProps({
    fields: [
      { name: `event`, value: event },
      { name: `url`, value: url },
      { name: `status`, value: status },
      { name: `percentage`, value: percentage },
      { name: `duration`, value: duration }
    ]
  });

  const eventData = {
    video_url: url,
    video_status: status,
    video_percent: percentage,
    video_duration: duration
  };

  dataLayerPush({ event, eventData });
};

export const dataLayerLogFunnel = ({ event, timelines }) => {
  checkProps({
    fields: [{ name: `event`, value: event }]
  });

  const eventData = timelines
    .reduce((prev, current) => {
      const { timeline } = current;

      const mapData = timeline
        .map((item) => {
          if (item.answerItems) {
            const answerActions = item.answerItems.map((answer) => {
              if (answer.journey !== null) {
                return answer.journey.title;
              }

              if (answer.result !== null) {
                return answer.result.title;
              }

              return `Invalid answer item entry (requires an action).`;
            });

            return answerActions;
          }

          if (item.questionNode) {
            return [item.questionNode.question];
          }

          return [item.text];
        })
        .reduce((p, n) => [...p, ...n], []);

      return [...prev, ...mapData];
    }, [])
    .map((name, id) => ({
      item_id: id,
      item_name: name
    }));

  dataLayerPush({ event, eventData });
};

export const dataLayerLogAnswer = ({
  event,
  question,
  questionNo,
  clickText
}) => {
  checkProps({
    fields: [
      { name: `event`, value: event },
      { name: `question`, value: question },
      { name: `question_no`, value: questionNo },
      { name: `click_text`, value: clickText }
    ]
  });

  const eventData = {
    question,
    question_no: questionNo,
    click_text: clickText
  };

  dataLayerPush({ event, eventData });
};
